import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于南宫集团',
  description: '南宫集团是一家多元化发展的综合性企业集团，业务涵盖科技研发、产业投资、国际贸易等多个领域。',
  mainEntity: {
    '@type': 'Organization',
    name: '南宫集团',
    url: 'https://www.nangong-group.com',
    description: '创新驱动发展，品质铸就未来。南宫集团致力于为全球客户提供卓越的产品与服务。',
    foundingDate: '2011',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号南宫大厦',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
};

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState('intro');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const url = 'https://readdy.ai/api/form/d9sn7l58o1vn02ekuib0';
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络连接异常，请稍后重试');
      setFormStatus('error');
    }
  };

  const tabs = [
    { key: 'intro', label: '品牌介绍' },
    { key: 'history', label: '发展历程' },
    { key: 'honor', label: '荣誉资质' },
    { key: 'contact', label: '联系我们' },
  ];

  const milestones = [
    { year: '2011', title: '集团创立', desc: '南宫集团在北京正式成立，以科技服务为起点开启创业征程。' },
    { year: '2013', title: '业务拓展', desc: '业务范围扩展至产业投资领域，成立第一支产业投资基金。' },
    { year: '2015', title: '全国布局', desc: '在上海、深圳、成都设立分支机构，完成全国重点城市战略布局。' },
    { year: '2017', title: '国际化起步', desc: '启动国际化战略，在新加坡、东京设立海外办事处。' },
    { year: '2019', title: '技术突破', desc: '成立AI研究院，大数据与人工智能技术取得多项突破性成果。' },
    { year: '2021', title: '规模跃升', desc: '集团总资产突破500亿元，员工总数超过8000人。' },
    { year: '2023', title: '生态构建', desc: '发布南宫生态战略，构建涵盖科技、投资、贸易的完整产业生态。' },
    { year: '2025', title: '全球视野', desc: '海外业务覆盖200+国家和地区，荣获多项国际权威奖项。' },
  ];

  const honors = [
    { title: '2025年度中国最具影响力企业品牌', org: '中国企业品牌发展论坛', year: '2025' },
    { title: '国家高新技术企业认证', org: '国家科技部', year: '2024' },
    { title: '中国民营企业500强', org: '中华全国工商业联合会', year: '2024' },
    { title: '最佳雇主品牌奖', org: '中国人力资源开发研究会', year: '2024' },
    { title: 'AAA级信用企业', org: '中国商业联合会', year: '2023' },
    { title: 'ESG优秀实践企业', org: '中国社会责任百人论坛', year: '2023' },
    { title: '中国科技创新领军企业', org: '中国科学技术协会', year: '2023' },
    { title: '行业数字化转型标杆', org: '工业和信息化部', year: '2022' },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '南宫集团主营业务是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '南宫集团是一家多元化发展的综合性企业集团，业务涵盖科技研发、产业投资、国际贸易等多个领域。集团旗下拥有云计算、AI大数据分析、物联网解决方案等科技业务，同时在新兴产业基金、不动产投资以及跨境供应链等方向拥有深厚布局。',
        },
      },
      {
        '@type': 'Question',
        name: '如何下载南宫集团官方APP？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以访问南宫集团官方网站首页，在APP下载专区选择iOS或Android版本进行下载。也可扫描官网提供的二维码直接下载，请认准官方渠道，谨防假冒应用。',
        },
      },
      {
        '@type': 'Question',
        name: '南宫集团的客服联系方式是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '南宫集团提供7×24小时客户服务，全国统一客服热线：400-888-9999，或发送邮件至contact@nangong-group.com，我们的地址位于北京市朝阳区建国路88号南宫大厦。',
        },
      },
      {
        '@type': 'Question',
        name: '南宫集团是否有投资业务？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，南宫集团在产业投资领域拥有广泛的布局，涵盖新兴产业基金（新能源、生物医药、人工智能）、商业地产、产业园区等多个方向，致力于为合作伙伴创造长期价值。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们-南宫集团-品牌介绍与联系方式"
        description="南宫集团关于我们页面，包含品牌介绍、发展历程、荣誉资质及联系方式。南宫集团是一家多元化发展的综合性企业集团，业务涵盖科技研发、产业投资、国际贸易等多个领域，总部位于北京。"
        keywords="南宫集团,关于我们,品牌介绍,南宫集团联系方式,南宫集团地址,南宫集团发展历程"
        canonicalPath="/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        <section className="py-8 md:py-12 bg-background-50">
          <div className="site-container px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">关于我们</h1>
            <p className="text-sm text-foreground-600 mb-8">了解南宫集团的品牌故事、发展历程与核心价值</p>

            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-10 max-w-lg overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 min-w-fit py-2.5 px-4 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'intro' && (
              <div className="max-w-4xl">
                <div className="bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10">
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">品牌介绍</h2>
                  <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      <strong>南宫集团</strong>成立于2011年，总部位于中国北京，是一家多元化发展的综合性企业集团。经过十余年的稳健发展，集团业务已覆盖<strong>科技研发</strong>、<strong>产业投资</strong>、<strong>国际贸易</strong>三大核心板块，形成了相互协同、互为支撑的产业生态格局。
                    </p>
                    <p>
                      在科技研发领域，南宫集团持续加大研发投入，建立了以人工智能、大数据、云计算和物联网为核心的技术体系。集团旗下拥有多个国家级高新技术企业，累计获得专利及软件著作权超过500项，为全球超过5000万用户提供智能化产品与服务。
                    </p>
                    <p>
                      在产业投资领域，南宫集团凭借敏锐的市场洞察力和专业的投资团队，在新兴产业、商业地产、产业园区等多个方向进行前瞻性布局，管理资产规模突破800亿元。
                    </p>
                    <p>
                      在国际贸易领域，南宫集团构建了覆盖200多个国家和地区的全球贸易网络，为合作伙伴提供跨境供应链管理、国际会展服务等一站式解决方案，年贸易额超过300亿元。
                    </p>
                    <p>
                      秉承<strong>"诚信为本、创新为魂"</strong>的经营理念，南宫集团始终坚持高质量发展道路，积极履行社会责任，先后荣获"中国最具影响力企业品牌"、"国家高新技术企业"、"中国民营企业500强"等多项殊荣。
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
                    <div className="text-center p-6 bg-background-100 rounded-xl">
                      <div className="text-2xl font-bold text-primary-600 font-heading">8000+</div>
                      <div className="text-xs text-foreground-600 mt-1">员工总数</div>
                    </div>
                    <div className="text-center p-6 bg-background-100 rounded-xl">
                      <div className="text-2xl font-bold text-primary-600 font-heading">500+</div>
                      <div className="text-xs text-foreground-600 mt-1">专利及软著</div>
                    </div>
                    <div className="text-center p-6 bg-background-100 rounded-xl">
                      <div className="text-2xl font-bold text-primary-600 font-heading">200+</div>
                      <div className="text-xs text-foreground-600 mt-1">覆盖国家与地区</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="max-w-4xl">
                <div className="bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10">
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-8">发展历程</h2>
                  <div className="relative">
                    <div className="absolute left-5 top-0 bottom-0 w-px bg-background-200"></div>
                    <div className="space-y-8">
                      {milestones.map((item, index) => (
                        <div key={index} className="relative pl-14">
                          <div className="absolute left-3.5 top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                          <span className="text-xs font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded">{item.year}</span>
                          <h3 className="text-base font-semibold text-foreground-900 mt-1.5 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-600">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'honor' && (
              <div className="max-w-4xl">
                <div className="bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10">
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-8">荣誉资质</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {honors.map((honor, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 flex-shrink-0">
                          <i className="ri-medal-line text-lg"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900">{honor.title}</h3>
                          <p className="text-xs text-foreground-500 mt-0.5">{honor.org}</p>
                          <span className="text-xs text-foreground-400">{honor.year}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="max-w-4xl">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-2 bg-background-50 rounded-2xl border border-background-200 p-8">
                    <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">联系方式</h2>
                    <div className="space-y-5">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500 mb-0.5">集团地址</div>
                          <div className="text-sm font-medium text-foreground-900">北京市朝阳区建国路88号南宫大厦</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                          <i className="ri-phone-line"></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500 mb-0.5">客服热线</div>
                          <div className="text-sm font-medium text-foreground-900">400-888-9999</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500 mb-0.5">电子邮箱</div>
                          <div className="text-sm font-medium text-foreground-900">contact@nangong-group.com</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                          <i className="ri-time-line"></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500 mb-0.5">工作时间</div>
                          <div className="text-sm font-medium text-foreground-900">周一至周五 9:00 - 18:00</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 p-4 bg-background-100 rounded-xl">
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        <i className="ri-information-line mr-1 text-accent-500"></i>
                        如需商务合作或媒体采访，请发送邮件至上述邮箱，我们的团队将在1-2个工作日内与您联系。
                      </p>
                    </div>

                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-3">关注我们</h3>
                      <div className="flex items-center gap-3">
                        <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                          <i className="ri-wechat-line"></i>
                        </a>
                        <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                          <i className="ri-weibo-line"></i>
                        </a>
                        <a href="#" className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer">
                          <i className="ri-linkedin-line"></i>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-3 bg-background-50 rounded-2xl border border-background-200 p-8">
                    <h2 className="text-xl font-bold font-heading text-foreground-900 mb-4">在线留言</h2>
                    <p className="text-sm text-foreground-600 mb-6">如您有任何疑问或建议，请填写以下表单，我们会尽快回复您。</p>

                    {formStatus === 'success' ? (
                      <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 text-center">
                        <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                          <i className="ri-check-line text-2xl text-accent-600"></i>
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">提交成功</h3>
                        <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快与您联系。</p>
                        <button
                          onClick={() => { setFormStatus('idle'); setFormError(''); }}
                          className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                        >
                          继续留言
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleContactSubmit} data-readdy-form="">
                        <input
                          name="phone_alt"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="honeypot-field"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名 *</label>
                            <input
                              id="contact-name"
                              name="name"
                              type="text"
                              required
                              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的姓名"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                            <input
                              id="contact-email"
                              name="email"
                              type="email"
                              required
                              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的邮箱"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="contact-phone" className="block text-xs font-medium text-foreground-700 mb-1.5">电话</label>
                            <input
                              id="contact-phone"
                              name="phone"
                              type="tel"
                              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的电话"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-company" className="block text-xs font-medium text-foreground-700 mb-1.5">公司</label>
                            <input
                              id="contact-company"
                              name="company"
                              type="text"
                              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                              placeholder="请输入您的公司名称"
                            />
                          </div>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={5}
                            className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                            placeholder="请输入您的留言内容，不超过500字"
                          ></textarea>
                          <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                        </div>
                        {formStatus === 'error' && (
                          <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
                            {formError}
                          </div>
                        )}
                        <button
                          type="submit"
                          className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-full text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-send-plane-line mr-2"></i>提交留言
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}